import React from 'react';
import { Building2, Loader2 } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useSubscription } from '@/hooks/useBillingAndUsage';

export interface OrganizationSwitcherProps {
  isCollapsed?: boolean;
}

export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ isCollapsed = false }) => {
  const { currentOrganization, isLoading, isSwitchingOrganization } = useOrganization();
  const { data: subscription } = useSubscription();
  const [imageError, setImageError] = React.useState(false);

  const logoUrl = currentOrganization?.logo_url;
  const orgId = currentOrganization?.id;

  React.useEffect(() => {
    setImageError(false);
  }, [logoUrl, orgId]);

  if (isLoading || isSwitchingOrganization) {
    return (
      <div className="flex items-center space-x-2 px-2 py-1.5">
        <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse">
          {isSwitchingOrganization && <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />}
        </div>
        {!isCollapsed && <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>}
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div
        className={`flex items-center px-2 py-1.5 text-gray-500 ${isCollapsed ? 'justify-center' : 'space-x-2'}`}
        title={isCollapsed ? 'No organization' : undefined}
      >
        <Building2 className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && <span className="text-sm">No organization</span>}
      </div>
    );
  }

  const activePlanName = subscription?.plan_name || currentOrganization?.plan_type || 'Free Trial';

  return (
    <div className={`flex items-center px-2 py-1.5 ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
      {logoUrl && !imageError ? (
        <div
          className="w-8 h-8 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center flex-shrink-0 bg-white shadow-sm"
          title={isCollapsed ? currentOrganization.name : undefined}
        >
          <img
            src={logoUrl}
            alt={currentOrganization.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div
          className="w-8 h-8 bg-[#A655F8] rounded-lg flex items-center justify-center flex-shrink-0"
          title={isCollapsed ? currentOrganization.name : undefined}
        >
          <Building2 className="w-4 h-4 text-white" />
        </div>
      )}
      {!isCollapsed && (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {currentOrganization.name}
          </p>
          <p className="text-xs text-gray-500 capitalize">
            {activePlanName} plan
          </p>
        </div>
      )}
    </div>
  );
};

export default OrganizationSwitcher;
